import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { casePath, isArchivedCase, type JobOrder } from '@/domain';
import { useSession } from '@/application/usecases/useSession';

/** The search parameter naming the case open over a page: `/doctors/12?case=JO-2041`. */
export const CASE_PARAM = 'case';

/**
 * The case a notification opened, over whatever screen the reader is on. Its
 * own word in the address, so it never meets the case a doctor's or a
 * patient's page has open under `case`. The request of 7 October 2026, section 2.
 */
export const NOTICE_PARAM = 'notice';

/** What a history entry remembers of the case open on it. */
interface CaseEntry {
  /** The entry was pushed to open the case, so going back one entry closes it. */
  casePushed?: boolean;
  /** The row that opened it was a case that has left the lab or was archived. */
  caseGone?: boolean;
}

/** What the entry remembers, whoever put it there: this hook's two words among it. */
function entryOf(state: unknown): CaseEntry {
  return typeof state === 'object' && state !== null ? state : {};
}

/** As much of a case as opening it reads: its number, and whether it is still in hand. */
export type OpenableCase = Pick<JobOrder, 'jobOrderNo' | 'dispatch' | 'archive'>;

export interface CaseInPlaceState {
  /** The case open over the page, by its job order number, or null. */
  jobOrderNo: string | null;
  /**
   * Whether this reader holds a screen that shows a case at all: Orders, or
   * the archive's record. Without either a row is not a thing to press, and a
   * link naming a case opens nothing.
   */
  mayOpen: boolean;
  /**
   * The row said the case has left the lab or was archived, so it is shown as
   * its record without first being asked for as an order. False for a case
   * opened by its link, which is then asked for.
   */
  gone: boolean;
  open: (order: OpenableCase) => void;
  /**
   * Opens a case known only by its number, as a notification knows it: read as
   * a case in hand first, and as a record when it turns out to have left.
   */
  openNumber: (jobOrderNo: string) => void;
  close: () => void;
}

/**
 * A case opened in place: over the doctor's or the patient's page it is listed
 * on, without leaving the page. The request of 6 October 2026 (fifth),
 * section 3.
 *
 * The case is named in the page's own address, `?case=JO-2041`, so the address
 * can be sent to somebody or opened again. Opening PUSHES that address onto
 * the history: the browser's Back, and a phone's, closes the case and lands on
 * the page, which never left the screen, so it is scrolled, filtered and
 * searched as it was. Closing by the button goes back through that same entry
 * when there is one; a case opened by its link has no entry behind it, and
 * closing then only takes the name out of the address. Lab Notes on the case
 * screen (`?notes=1`) opens and closes the same way.
 *
 * Every other key of the address is left alone, which is how the page's tab,
 * filters and search survive a case being opened over them.
 */
export function useCaseInPlace(param: string = CASE_PARAM): CaseInPlaceState {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const views = useSession().data?.views;

  const mayOpen = casePath(views ?? [], { jobOrderNo: '' }, 'order') !== null;
  const named = params.get(param);
  const jobOrderNo = named === null || named.trim() === '' ? null : named;
  const entry = entryOf(location.state);
  const pushed = entry.casePushed === true;
  const state: unknown = location.state;

  const open = useCallback(
    (order: OpenableCase) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(param, order.jobOrderNo);
          return next;
        },
        {
          state: {
            ...entryOf(state),
            casePushed: true,
            caseGone: order.dispatch.dispatched || isArchivedCase(order),
          },
        },
      );
    },
    [setParams, state, param],
  );

  const openNumber = useCallback(
    (number: string) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(param, number);
          return next;
        },
        { state: { ...entryOf(state), casePushed: true, caseGone: false } },
      );
    },
    [setParams, state, param],
  );

  const close = useCallback(() => {
    // Asked of a closed panel too: Escape reaches the drawer whether or not it is open.
    if (jobOrderNo === null) return;
    if (pushed) {
      void navigate(-1);
      return;
    }
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(param);
        return next;
      },
      { replace: true, state },
    );
  }, [jobOrderNo, pushed, navigate, setParams, state]);

  return {
    jobOrderNo,
    mayOpen,
    gone: jobOrderNo !== null && entry.caseGone === true,
    open,
    openNumber,
    close,
  };
}
